import { useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import {
  createAssignments,
  deleteAssignment,
  replaceAssignment,
} from "@/api/form-builder-assignments/formBuilderAssignments.mutations";
import {
  FormBuilderAssignmentsQueryKeys,
  getAssignmentOptions,
  getAssignmentsTree,
  type AssignmentOptionsScope,
} from "@/api/form-builder-assignments/formBuilderAssignments.queries";
import { handleApiError } from "@/utils/responseHandler";
import type { ApiError } from "@/types/common.types";
import type {
  AssignmentClientGroup,
  AssignmentEntityNode,
  AssignmentsTreeParams,
  AssignmentsTreeResponse,
  CreateAssignmentPayload,
} from "@/api/form-builder-assignments/formBuilderAssignments.types";

const LOAD_MORE_PAGE_SIZE = 10;
export const INITIAL_NESTED_LIMIT = 2;

export const useAssignmentsTree = (
  params: AssignmentsTreeParams = {},
  options: Pick<UseQueryOptions<AssignmentsTreeResponse>, "placeholderData"> = {},
) => {
  return useQuery({
    queryKey: [FormBuilderAssignmentsQueryKeys.tree, params],
    queryFn: ({ signal }) => getAssignmentsTree(params, signal),
    ...options,
  });
};

const findEntity = (tree: AssignmentsTreeResponse, entityId: string | null) =>
  tree.entities.find((node) => node.entityId === entityId) ?? tree.entities[0];

// Extra windows stay disabled until the first click so nothing is fetched up front, then stay
// enabled so tree invalidation after a mutation refetches every page already loaded.
// `search` must match the filter behind the parent's totals, or the offsets drift.
export const useEntityAssignmentClients = (entity: AssignmentEntityNode, search?: string) => {
  const [started, setStarted] = useState(false);
  const initialOffset = entity.clients.length;
  const query = useInfiniteQuery({
    queryKey: [
      FormBuilderAssignmentsQueryKeys.tree,
      "entityClients",
      entity.entityId,
      search ?? "",
      initialOffset,
    ],
    queryFn: ({ pageParam, signal }) =>
      getAssignmentsTree(
        {
          tenantId: entity.entityId ?? undefined,
          clientOffset: pageParam,
          clientLimit: LOAD_MORE_PAGE_SIZE,
          brandLimit: INITIAL_NESTED_LIMIT,
          ...(search ? { search } : {}),
        },
        signal,
      ),
    initialPageParam: initialOffset,
    getNextPageParam: (lastPage, _allPages, lastOffset) => {
      const node = findEntity(lastPage, entity.entityId);
      const nextOffset = lastOffset + (node?.clients.length ?? 0);
      return node && node.clients.length > 0 && nextOffset < node.clientsTotal
        ? nextOffset
        : undefined;
    },
    enabled: started && !!entity.entityId,
  });

  const loadedIds = new Set(entity.clients.map((client) => client.clientId));
  const extraClients = (query.data?.pages ?? [])
    .flatMap((page) => findEntity(page, entity.entityId)?.clients ?? [])
    .filter((client) => !loadedIds.has(client.clientId) && loadedIds.add(client.clientId));
  const clients = [...entity.clients, ...extraClients];

  return {
    clients,
    hasMore: clients.length < entity.clientsTotal && (!query.data || query.hasNextPage),
    isLoadingMore: query.isFetching,
    loadMore: () => (started ? void query.fetchNextPage() : setStarted(true)),
  };
};

export const useClientAssignmentBrands = (
  entityId: string | null,
  client: AssignmentClientGroup,
  search?: string,
) => {
  const [started, setStarted] = useState(false);
  const initialOffset = client.brands.length;
  const findClient = (tree: AssignmentsTreeResponse) =>
    findEntity(tree, entityId)?.clients.find((node) => node.clientId === client.clientId);
  const query = useInfiniteQuery({
    queryKey: [
      FormBuilderAssignmentsQueryKeys.tree,
      "clientBrands",
      entityId,
      client.clientId,
      search ?? "",
      initialOffset,
    ],
    queryFn: ({ pageParam, signal }) =>
      getAssignmentsTree(
        {
          tenantId: entityId ?? undefined,
          clientId: client.clientId,
          brandOffset: pageParam,
          brandLimit: LOAD_MORE_PAGE_SIZE,
          ...(search ? { search } : {}),
        },
        signal,
      ),
    initialPageParam: initialOffset,
    getNextPageParam: (lastPage, _allPages, lastOffset) => {
      const node = findClient(lastPage);
      const nextOffset = lastOffset + (node?.brands.length ?? 0);
      return node && node.brands.length > 0 && nextOffset < node.brandsTotal
        ? nextOffset
        : undefined;
    },
    enabled: started && !!entityId,
  });

  const loadedIds = new Set(client.brands.map((brand) => brand.brandId));
  const extraBrands = (query.data?.pages ?? [])
    .flatMap((page) => findClient(page)?.brands ?? [])
    .filter((brand) => !loadedIds.has(brand.brandId) && loadedIds.add(brand.brandId));
  const brands = [...client.brands, ...extraBrands];

  return {
    brands,
    hasMore: brands.length < client.brandsTotal && (!query.data || query.hasNextPage),
    isLoadingMore: query.isFetching,
    loadMore: () => (started ? void query.fetchNextPage() : setStarted(true)),
  };
};

export const useAssignmentOptions = (
  scope: Omit<AssignmentOptionsScope, "tenantId"> & { tenantId?: string },
) => {
  return useQuery({
    queryKey: [FormBuilderAssignmentsQueryKeys.options, scope],
    queryFn: ({ signal }) => getAssignmentOptions({ ...scope, tenantId: scope.tenantId! }, signal),
    enabled: !!scope.tenantId,
  });
};

function useInvalidateAssignmentsTree() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [FormBuilderAssignmentsQueryKeys.tree] });
}

export const useReplaceAssignment = () => {
  const invalidateTree = useInvalidateAssignmentsTree();
  return useMutation({
    mutationFn: (variables: { create: CreateAssignmentPayload; deleteId?: string }) =>
      replaceAssignment(variables),
    onSuccess: invalidateTree,
    onError: (error) => handleApiError(error as ApiError, "Failed to update assignment"),
  });
};

export const useCreateAssignments = () => {
  const invalidateTree = useInvalidateAssignmentsTree();
  return useMutation({
    mutationFn: (payloads: CreateAssignmentPayload[]) => createAssignments(payloads),
    onSuccess: invalidateTree,
    onError: (error) => handleApiError(error as ApiError, "Failed to create assignments"),
  });
};

export const useDeleteAssignment = () => {
  const invalidateTree = useInvalidateAssignmentsTree();
  return useMutation({
    mutationFn: ({ assignmentId, tenantId }: { assignmentId: string; tenantId: string }) =>
      deleteAssignment(assignmentId, tenantId),
    onSuccess: invalidateTree,
    onError: (error) => handleApiError(error as ApiError, "Failed to remove assignment"),
  });
};
